import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2, LogOut, AlertCircle, HelpCircle, X } from 'lucide-react';
import i18n from '../../i18n/index.js';

const tr = (cle, vars) => i18n.t(`confirm_dialog.${cle}`, vars);

/**
 * ConfirmDialog — Premium modal replacement for native browser confirmations.
 *
 * Displays a centered, animated confirmation dialog with customizable title, message, and action buttons.
 * Supports 5 variants (danger, warning, logout, info, default) with semantic icons and colors.
 * Fully accessible with ARIA attributes, keyboard support (Escape key), focus management, and touch-friendly targets.
 *
 * @example
 * const [dlg, setDlg] = useState(null);
 * setDlg({
 *   title: 'Delete User?',
 *   message: 'This action cannot be undone.',
 *   variant: 'danger',
 *   onConfirm: () => deleteUser(),
 * });
 * <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
 *
 * Or use the hook:
 * const { confirm, element } = useConfirm();
 * await confirm({ title: 'Continue?', variant: 'warning' });
 */

// Les fonds des boutons de confirmation sont TOUJOURS des couleurs saturées :
// le texte est donc blanc explicite (#ffffff) pour un contraste WCAG garanti,
// indépendamment du token de marque (--color-primary-foreground) qui peut varier.
// Libellés `confirm` résolus via i18n au rendu (clé, pas texte — cf. tr() plus bas).
const VARIANTS = {
  danger:  { icon: Trash2,        color: 'var(--color-danger)',  bg: 'var(--danger-bg)',  confirmKey: 'variant_danger_confirm',  confirmStyle: { background: 'var(--color-danger)',  color: '#ffffff' } },
  warning: { icon: AlertTriangle, color: 'var(--color-warning)', bg: 'var(--warn-bg)',    confirmKey: 'variant_warning_confirm', confirmStyle: { background: 'var(--color-warning)', color: '#ffffff' } },
  logout:  { icon: LogOut,        color: 'var(--color-danger)',  bg: 'var(--danger-bg)',  confirmKey: 'variant_logout_confirm',  confirmStyle: { background: 'var(--color-danger)',  color: '#ffffff' } },
  info:    { icon: AlertCircle,   color: 'var(--color-info)',    bg: 'var(--info-bg)',    confirmKey: 'variant_default_confirm', confirmStyle: { background: 'var(--color-primary)', color: '#ffffff' } },
  default: { icon: HelpCircle,    color: 'var(--color-primary)', bg: 'var(--primary-bg)', confirmKey: 'variant_default_confirm', confirmStyle: { background: 'var(--color-primary)', color: '#ffffff' } },
};

const ConfirmDialog = ({ dialog, onClose }) => {
  useTranslation(); // abonnement au changement de langue (tr() résolu via i18n)
  const cancelRef = useRef(null);
  // Confirmation renforcée : quand dialog.requireText est fourni, l'utilisateur
  // doit le retaper exactement pour débloquer le bouton de confirmation.
  const [typed, setTyped] = React.useState('');
  const [prevDialog, setPrevDialog] = React.useState(dialog);
  if (dialog !== prevDialog) {
    // Réinitialisation pendant le rendu (pattern React "adjust state on prop change")
    setPrevDialog(dialog);
    setTyped('');
  }

  useEffect(() => {
    if (!dialog) return;
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onClose]);

  if (!dialog) return null;

  const v = VARIANTS[dialog.variant] || VARIANTS.default;
  const Icon = v.icon;
  const confirmLabel = dialog.confirmLabel || tr(v.confirmKey);
  const textOk = !dialog.requireText || typed.trim() === dialog.requireText;

  const handleConfirm = () => {
    if (!textOk) return;
    dialog.onConfirm?.();
    onClose('confirm');
  };

  const dialogElement = (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          zIndex: 100000,
          animation: 'fadeIn 0.15s ease',
        }}
      />

      {/* Dialog */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cdlg-title"
        aria-describedby="cdlg-body"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100001,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '28px 28px 24px',
          width: 'min(440px, calc(100vw - 32px))',
          boxShadow: 'var(--shadow-lg)',
          animation: 'dialogIn 0.2s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        {/* Close X */}
        <button
          onClick={onClose}
          aria-label={tr('close_aria_label')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-text-3)',
            padding: 4,
            borderRadius: 8,
            minHeight: '44px',
            minWidth: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--surface2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={18} />
        </button>

        {/* Icon badge */}
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: v.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <Icon size={24} style={{ color: v.color }} />
        </div>

        <div id="cdlg-title" style={{ fontWeight: 700, fontSize: 17, color: 'var(--color-text-1)', marginBottom: 8 }}>
          {dialog.title || tr('title_fallback')}
        </div>

        <div id="cdlg-body" style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: dialog.requireText ? 14 : 24 }}>
          {dialog.message || tr('message_fallback')}
        </div>

        {dialog.requireText && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-3)', marginBottom: 6 }}>
              {tr('type_to_confirm_prefix')} <span style={{ fontFamily: 'monospace', color: v.color, fontWeight: 800 }}>{dialog.requireText}</span> {tr('type_to_confirm_suffix')}
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && textOk) { e.preventDefault(); handleConfirm(); } }}
              placeholder={dialog.requireText}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${textOk && typed ? v.color : 'var(--border)'}`,
                background: 'var(--surface2)', color: 'var(--color-text-1)',
                fontSize: 14, fontFamily: 'monospace', outline: 'none',
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            ref={cancelRef}
            onClick={onClose}
            aria-label={dialog.cancelLabel || tr('cancel_aria_label_fallback')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 500,
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              color: 'var(--color-text-1)',
              cursor: 'pointer',
              minHeight: 44,
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--surface3)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--surface2)'}
          >
            {dialog.cancelLabel || i18n.t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!textOk}
            aria-label={tr('confirm_aria_label', { confirmLabel, title: dialog.title || tr('title_fallback') })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleConfirm();
              }
            }}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              border: 'none',
              minHeight: 44,
              whiteSpace: 'nowrap',
              transition: 'opacity 0.2s ease',
              ...v.confirmStyle,
              opacity: textOk ? 1 : 0.45,
              cursor: textOk ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn   { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dialogIn { from { opacity: 0; transform: translate(-50%,-50%) scale(0.92) } to { opacity: 1; transform: translate(-50%,-50%) scale(1) } }
      `}</style>
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(dialogElement, document.body)
    : dialogElement;
};

ConfirmDialog.propTypes = {
  dialog: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    variant: PropTypes.oneOf(['danger', 'warning', 'logout', 'info', 'default']),
    requireText: PropTypes.string,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    onConfirm: PropTypes.func,
  }),
  onClose: PropTypes.func.isRequired,
};

ConfirmDialog.defaultProps = {
  dialog: null,
};

const ConfirmContext = React.createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = React.useState(null);
  const resolverRef = React.useRef(null);

  const confirm = React.useCallback((options, maybeMessage) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      const opts = typeof options === 'string'
        ? (maybeMessage ? { title: options, message: maybeMessage, variant: 'danger' } : { message: options, variant: 'danger' })
        : (options || {});
      setDialog({
        variant: opts.danger ? 'danger' : (opts.variant || 'danger'),
        ...opts,
        onConfirm: () => {
          resolverRef.current?.(true);
          resolverRef.current = null;
        },
      });
    });
  }, []);

  const close = React.useCallback((reason = 'cancel') => {
    if (reason !== 'confirm') {
      resolverRef.current?.(false);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  const contextValue = React.useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog dialog={dialog} onClose={close} />
    </ConfirmContext.Provider>
  );
};

ConfirmProvider.propTypes = {
  children: PropTypes.node,
};

ConfirmProvider.defaultProps = {
  children: null,
};

export default ConfirmDialog;

/* ── useConfirm hook — usage simplifié ────────────────────── */
// eslint-disable-next-line react-refresh/only-export-components
export const useConfirm = () => {
  const context = React.useContext(ConfirmContext);
  const [dialog, setDialog] = React.useState(null);
  const resolverRef = React.useRef(null);

  const localConfirm = React.useCallback((options, maybeMessage) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      const opts = typeof options === 'string'
        ? (maybeMessage ? { title: options, message: maybeMessage, variant: 'danger' } : { message: options, variant: 'danger' })
        : (options || {});
      setDialog({
        variant: opts.danger ? 'danger' : (opts.variant || 'danger'),
        ...opts,
        onConfirm: () => {
          resolverRef.current?.(true);
          resolverRef.current = null;
        },
      });
    });
  }, []);

  const localClose = React.useCallback((reason = 'cancel') => {
    if (reason !== 'confirm') {
      resolverRef.current?.(false);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  if (context?.confirm) {
    return {
      confirm: context.confirm,
      element: null,
    };
  }

  const element = <ConfirmDialog dialog={dialog} onClose={localClose} />;

  return { confirm: localConfirm, element };
};

